import Head from 'next/head'
import { useEffect, useState } from 'react'
import {
  useQueryState,
  useResponsive,
  useSiteTitle,
  useTranslations,
} from '@dustin-web/microsite-components/src/hooks'
import { getSortBy, useProductListSettings } from '@dustin-web/product-components/src/product-list'
import { useGraphql } from '@dustin-web/microsite-data/src/hooks'
import { getBasePath } from '@dustin-web/microsite-nextjs/src/utils/server-utils'
import { getProductUrl } from '@dustin-web/microsite-components/src/utils/urls'
import { constants } from '@dustin-web/microsite-config'
import { log } from '@dustin-web/microsite-config/src/logger'
import useCleanRouter from '@dustin-web/microsite-components/src/hooks/use-clean-router'
import type {
  GetLandingPageQuery,
  QueryLandingPageArgs,
} from '@dustin-web/microsite-data/src/graphql/product/generated/graphql-types'
import type { PageGetServerSidePropsContext } from '@dustin-web/microsite-nextjs/src/with-server-data/with-microsite-props'
import {
  type QuerySearchPageArgs,
  Origin,
  Touchpoint,
  SearchPageQuery,
} from '@productsite/src/graphql/generated/graphql-types'
import { GET_LANDING_PAGE } from '@dustin-web/microsite-data/src/graphql/product/get-landing-page.graphql'
import { buildQueryState } from '@dustin-web/microsite-components/src/utils/querystate'
import { infinityScrollPageSize } from '@productsite/src/components/utils/infinity-scroll/use-infinity-scroll'
import { SearchPage } from '@productsite/src/graphql/search.graphql'
import SearchPageLayout from '../search-page-layout'

const getCategoryPathFromPath = (path: string) => {
  const categoryFromPath = path.split('/').slice(3).join('/').replace(/\/?$/, '/')

  if (categoryFromPath.length <= 1) {
    return null
  }

  return categoryFromPath
}

export const Page = ({
  searchPhrase,
  isUserInitiated,
}: {
  searchPhrase: string
  isUserInitiated: boolean
}) => {
  const { queryState } = useQueryState()
  const { isSmallScreen } = useResponsive()
  const { sortBy } = useProductListSettings()
  const { translate } = useTranslations()
  const { getSiteTitle } = useSiteTitle()
  const { pageUriWithoutQuery } = useCleanRouter()

  const { activeFilters: facets, page } = queryState
  const selectedCategoryPath = getCategoryPathFromPath(pageUriWithoutQuery)
  const phrase = pageUriWithoutQuery.split('/')[2] || searchPhrase 
  const decodedSearchPrase = phrase ? decodeURIComponent(phrase) : ''
  const isValidSearch = !!decodedSearchPrase

  const { isValidating: loading, data: fetchedData } = useGraphql<
    SearchPageQuery,
    QuerySearchPageArgs
  >(SearchPage, {
    variables: {
      isUserInitiated,
      page,
      pageSize: infinityScrollPageSize,
      searchPhrase: decodedSearchPrase,
      selectedCategoryPath,
      origin: Origin.Organic,
      touchpoint: (isSmallScreen && Touchpoint.Mobile) || Touchpoint.Desktop,
      facets: facets,
      sortBy: getSortBy(sortBy),
    },
    context: {
      skip: !isValidSearch,
    },
  })

  const [data, setData] = useState(fetchedData?.searchPage)

  useEffect(() => {
    if (!loading && fetchedData) {
      setData(fetchedData.searchPage)
    }
  }, [loading, setData, fetchedData])

  if (!data) {
    return
  }

  return (
    <>
      <Head>
        <title>
          {getSiteTitle(
            translate('Search_Page_Title', {
              searchPhraseString: `"${decodedSearchPrase}"`,
            })
          )}
        </title>
        {isUserInitiated && <meta name="robots" content="noindex" />}
      </Head>
      {isValidSearch && data ? (
        <SearchPageLayout
          searchPage={data}
          loading={loading}
          searchPhrase={decodedSearchPrase}
          selectedCategoryPath={selectedCategoryPath}
          isUserInitiated={isUserInitiated}
        />
      ) : (
        <div className="container">{translate('InvalidSearch')}</div>
      )}
    </>
  )
}

export const E4getServerSideProps = async ({
  micrositeProps,
}: Pick<PageGetServerSidePropsContext, 'micrositeProps'>) => {
  const { cookies, req, fetchGraphql, query, requestContext } = micrositeProps ?? {}
  const basePath = getBasePath(req).basePath
  const isUserInitiated = basePath?.toLowerCase() !== 'search'
  const selectedCategoryPath = getCategoryPathFromPath(req?.pathname || '')
  const searchPhrase = !query?.path?.length
    ? ''
    : Array.isArray(query?.path) // check if array because it can be a string
      ? decodeURI(query?.path[0])
      : decodeURI(query?.path)

  const initialProps = {
    isUserInitiated,
    searchPhrase,
    selectedCategoryPath,
  }

  const props = {
    ...initialProps,
    fallback: {},
  }

  const isValidSearch = !!searchPhrase
  const { activeFilters: facets, page } = buildQueryState(query)
  const sortBy = cookies[constants.cookies.userSettings.sortBy] || 'sales'
  const touchpoint =
    (requestContext.deviceType === 'mobile' && Touchpoint.Mobile) || Touchpoint.Desktop

  try {
    const [key, data] = await fetchGraphql<SearchPageQuery, QuerySearchPageArgs>(SearchPage, {
      variables: {
        isUserInitiated,
        page,
        pageSize: infinityScrollPageSize,
        searchPhrase: searchPhrase,
        selectedCategoryPath,
        origin: Origin.Organic,
        touchpoint,
        facets: facets,
        sortBy: getSortBy(sortBy),
      },
      context: {
        skip: !isValidSearch,
      },
    })

    const [landingPageKey, landingPageData] = await fetchGraphql<
      GetLandingPageQuery,
      QueryLandingPageArgs
    >(GET_LANDING_PAGE, {
      variables: {
        pageSize: 100,
        page: 1,
        pageReference: '/no-search-results',
        touchpoint,
        sortBy: getSortBy(sortBy),
      },
      context: {
        skip: false,
      },
    })

    props.fallback = {
      [key]: data,
      [landingPageKey]: landingPageData,
    }

    const { searchPage } = data ?? {}

    if (searchPage) {
      const firstProduct = searchPage?.productReferences?.[0]?.searchResultProduct
      const hasNoFilters =
        Object.entries(query)?.filter(([key, _]) => key !== 'path' && !key?.includes('Global.'))
          .length === 0

      if (searchPage?.hits === 1 && firstProduct && hasNoFilters) {
        const url = getProductUrl(firstProduct?.id, firstProduct?.nameSlug)

        return {
          props: props,
          redirect: {
            destination: url,
            permanent: false,
          },
        }
      }
    }
  } catch (err) {
    log.error(
      { err, traceIdentifier: requestContext.correlationId },
      `Search page error: ${JSON.stringify(err)}`
    )
  }

  return { props }
}
